import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ExamineeService } from '../../services/examinee.service';
import { StreamService } from '../../services/stream.service';
import { Examinee } from '../../shared/models/examinee.model';
import { Chapter, Question, Questionnaire } from '../../shared/models/questionnaire.model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, Subscription, timer } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { OfflineAnswerService } from '../../services/offline-answer.service';

@Component({
  selector: 'app-assessment-question',
  templateUrl: './assessment-question.component.html',
  styleUrl: './assessment-question.component.scss'
})
export class AssessmentQuestionComponent implements OnInit, OnDestroy {
	private saveAnswerSubscription!: Subscription;

	@ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
	private videoStream!: MediaStream;
	private mediaRecorder!: MediaRecorder;
	elapsedTime = 0;
	showVideoUploading = false;

	uploadInProgress = true;
	savingOfflineAnswer = true;
	progressValue = 0;

	examinee!: Examinee;
	questionnaire!: Questionnaire;
	currentChapter!: Chapter;
	currentQuestion!: Question;

	sanitizedContent: any;
	safeUrl!: SafeResourceUrl;

	currentAction: any;
	currentQuestionSubtitle = '';

	selectedOptions: any[] = [];
	selectedRangeOption: any = null;
	record_timer = 0;

	chapter_name = '';
	chapter_index = 0;
	question_index = 0;
	question_length = 0;

	loading = false;
	allowNext = false;

	showTimer = false;
	showAutoRecordTimer = false;

	question_start_time = 0;

	voice_start_time = 0;
	voice_end_time = 0;

	showBaseline = true;

	passConditionsObserve!: Observable<boolean>;
	passConditionObservable!: Subscription;
	condition = true;

	timeRemaining: number | undefined = undefined;
	timeLimitSubscription!: Subscription;
	recordLimitSubscription!: Subscription;
	isRecording = false;
	isRecorded = false;
	waitTimeout = 0;
	waitRecordAutoStart = 0;
	recordAutoStartTimeRemaining = 0;
	currentQuestion_time_limit = 0;

	conditionNotPassed: number[] = [];

	private offlineAnswersSubscription: Subscription | null = null;
	pendingOfflineAnswers = false;
	pendingOfflineAnswersLen = 0;

	constructor(private translate: TranslateService, private sanitizer: DomSanitizer, private streamService: StreamService, private examineeService: ExamineeService, private offlineAnswerService: OfflineAnswerService) { }

	ngOnInit(): void {
		this.passConditionsObserve = this.streamService.passConditions$;

		this.passConditionObservable = this.passConditionsObserve.subscribe(pass => {
			this.condition = pass;

			if (pass === false) {
				this.conditionNotPassed.push(this.elapsedTime);
			}
    });

		this.streamService.uploadProgressObs.subscribe((data) => {
			if (data.finishUploading) {
				this.uploadInProgress = false;
			}

			this.progressValue = data.progress;
		});

		this.offlineAnswersSubscription = this.offlineAnswerService.hasPendingAnswers$.subscribe(
      (hasPending) => {
        this.pendingOfflineAnswers = hasPending;
        this.pendingOfflineAnswersLen = this.offlineAnswerService.pendingAnswers();
      }
    );

		this.startRecording();
		this.examinee = this.examineeService.examineeValue;
		this.streamService.examinee_id = this.examinee.id;
		this.questionnaire = this.examineeService.questionnaireValue;
		this.currentQuestion = this.questionnaire.baseline.baseline_questions[this.question_index];
		this.question_length = this.questionnaire.baseline.baseline_questions.length;

		if (this.questionnaire.lang_code === 'he') {
			this.translate.use('he');
		}

		this.setCurrentQuestion();
		this.handleQuestionResponse();
		this.startTimer();
	}

	ngOnDestroy(): void {
    if (this.saveAnswerSubscription) {
      this.saveAnswerSubscription.unsubscribe();
    }

    if (this.passConditionObservable) {
      this.passConditionObservable.unsubscribe();
    }

    if (this.offlineAnswersSubscription) {
      this.offlineAnswersSubscription.unsubscribe();
    }
  }

	startRecording() {
		navigator.mediaDevices.getUserMedia({ 
			video: this.streamService.selectedCamera ? { deviceId: this.streamService.selectedCamera } : true,
			audio: this.streamService.selectedMicrophone ? { deviceId: this.streamService.selectedMicrophone } : true
		})
		.then(async (stream) => {
			if (!stream) {
				this.examineeService.toastErrorMessage('Video stream not available.', 'Error!');
				console.error('Video stream not available. Please grant access to media devices.');
				return;
			}
			
			this.elapsedTime = 0;

			this.videoStream = stream;
			this.setVideoElement();
			this.mediaRecorder = new MediaRecorder(stream, {
				mimeType: 'video/webm; codecs=vp9',
				audioBitsPerSecond: 128000,
				videoBitsPerSecond : 4500000,
			});

			this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
				if (event.data && event.data.size > 0) {
					this.streamService.storeChunk(event.data);
				}
			};
	
			this.mediaRecorder.start(10000);
			this.streamService.isRecording = true;
			this.streamService.processUploadQueue();

			this.streamService.checkConditions(this.videoPlayer.nativeElement);
		})
		.catch(error => console.error('Error accessing camera:', error));
	}

	setVideoElement() {
		this.videoPlayer.nativeElement.srcObject = this.videoStream;
    this.videoPlayer.nativeElement.addEventListener('timeupdate', this.onTimeUpdate.bind(this));
		this.videoPlayer.nativeElement.play();
  }

	onTimeUpdate() {
		this.elapsedTime = this.videoPlayer.nativeElement.currentTime * 1000;
  }

	saveAnswer() {
		const currentQuestionEndTime = this.elapsedTime;

		const answer = {
			chapter_id: this.showBaseline ? this.questionnaire.baseline.id : this.questionnaire.chapters[this.chapter_index].id,
			chapter_type: this.showBaseline ? 'BASELINE' : 'REGULAR',
			question_id: this.showBaseline ? this.questionnaire.baseline.baseline_questions[this.question_index].id : this.questionnaire.chapters[this.chapter_index].questions[this.question_index].id,
			time_start: this.question_start_time,
			time_end: currentQuestionEndTime,
			examinee_id: this.examinee.id,
			answer: this.getResponseAnswer()
		}

		this.question_start_time = this.elapsedTime;

		this.saveAnswerSubscription = this.examineeService.saveAnswer(answer).subscribe({
      next: () => {
        // Answer saved successfully
      },
      error: (err: any) => {
        console.error('Error saving answer:', err);
        this.offlineAnswerService.addFailedAnswer(answer);
      }
    });
	}

	setCurrentQuestion() {
		if (this.currentQuestion.body.question && this.currentQuestion.body.question != '<p></p>') {
			this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(this.currentQuestion.body.question);
		} else {
			this.sanitizedContent = null;
		}

		if (this.currentQuestion.body.link_url) {
			this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.currentQuestion.body.link_url);
		}
	}

	nextQuestion() {
		this.saveAnswer();
		this.resetVariables();

		if (!this.showBaseline && this.chapter_index === (this.questionnaire.chapters.length-1) && this.question_index === (this.questionnaire.chapters[this.chapter_index].questions.length-1)) {
			this.stopRecording();
			return;
		}

		if (this.showBaseline) {
			if (this.question_index === this.questionnaire.baseline.baseline_questions.length-1) {
				this.question_index = 0;
				this.showBaseline = false;
				this.chapter_name = this.questionnaire.chapters[this.chapter_index].name ? this.questionnaire.chapters[this.chapter_index].name : 'Chapter-' + (this.chapter_index+1)
				this.question_length = this.questionnaire.chapters[this.chapter_index].questions.length;
				this.currentQuestion = this.questionnaire.chapters[this.chapter_index].questions[this.question_index];
				this.setCurrentQuestion();
			} else {
				this.question_index++;
				this.currentQuestion = this.questionnaire.baseline.baseline_questions[this.question_index];
				this.setCurrentQuestion();
			}
		} else {
			if (this.question_index === this.questionnaire.chapters[this.chapter_index].questions.length-1) {
				this.question_index = 0;
				this.chapter_index++;
				this.chapter_name = this.questionnaire.chapters[this.chapter_index].name ? this.questionnaire.chapters[this.chapter_index].name : 'Chapter-' + (this.chapter_index+1)
				this.question_length = this.questionnaire.chapters[this.chapter_index].questions.length;
				this.currentQuestion = this.questionnaire.chapters[this.chapter_index].questions[this.question_index];
				this.setCurrentQuestion();
			} else {
				this.question_index++;
				this.currentQuestion = this.questionnaire.chapters[this.chapter_index].questions[this.question_index];
				this.setCurrentQuestion();
			}
		}

		this.handleQuestionResponse();
		this.startTimer();
	}

	handleQuestionResponse() {
    switch (this.currentQuestion.response.response_type) {
      case 'RANGE':
        this.currentAction = 'SELECT_AN_ANSWER';
        this.currentQuestionSubtitle = '';
        break;
      case 'SELECT':
        this.currentAction = this.currentQuestion.response.response_select?.multi_select ? 'MULTI_SELECT' : 'SINGLE_SELECT';
        this.currentQuestionSubtitle = '';
        break;
      case 'VISUAL':
        this.currentAction = '';
        this.currentQuestionSubtitle = '';
        break;
      case 'VOICE':
        this.currentAction = 'RECORD_TITLE';
        this.currentQuestionSubtitle = 'RECORD_SUB_TITLE';
				if (this.currentQuestion.response.response_voice) {
					this.record_timer = this.currentQuestion.response.response_voice.record_sec;
				}
        break;
    }
  }

	startVoiceRecording() {
		this.isRecording = true;
		this.showAutoRecordTimer = false;
		this.voice_start_time = this.elapsedTime;
		this.recordLimitSubscription = timer(1000, 1000).subscribe(() => {
			if (this.record_timer > 0) {
				this.record_timer--;
			} else {
				this.isRecording = false;
				this.isRecorded = true;
				this.stopVoiceRecording();
			}
		});
	}

	stopVoiceRecording() {
		this.recordLimitSubscription.unsubscribe();
		this.voice_end_time = this.elapsedTime;
		this.record_timer = 0;
		this.isRecording = false;
		this.isRecorded = true;
		this.allowNext = true;
	}

	startTimer() {
		if (this.showVideoUploading) {
			return;
		}

		if (this.currentQuestion.response.response_type === 'VISUAL') {
			setTimeout(() => {
				this.allowNext = true;
			}, 5000);
		}

		if (this.currentQuestion.response.response_type === 'VOICE') {
			if (this.currentQuestion.response.response_voice) {
				this.waitRecordAutoStart = this.currentQuestion.response.response_voice.alert_before > 0 ? this.currentQuestion.response.response_voice.alert_before : 5;
				this.recordAutoStartTimeRemaining = this.waitRecordAutoStart;
			}
		}

		this.waitTimeout = this.currentQuestion.count_down+1;

		this.timeLimitSubscription = timer(1000, 1000).subscribe(() => {
			if (this.timeRemaining === undefined) {
				this.currentQuestion_time_limit = this.currentQuestion.time_limit > 0 ? this.currentQuestion.time_limit : 10;
				this.timeRemaining = this.currentQuestion_time_limit;
			} else {
				this.timeRemaining--;
				
				if (this.timeRemaining <= this.currentQuestion.count_down+1) {
					this.waitTimeout--;
					this.showTimer = true;
				}

				if (this.currentQuestion.response.response_type === 'VOICE') {
					const check = this.timeRemaining <= (this.currentQuestion_time_limit - this.waitRecordAutoStart) && this.timeRemaining >= (this.currentQuestion_time_limit - (this.waitRecordAutoStart*2));
					
					if (check && !this.isRecording && !this.isRecorded) {
						this.recordAutoStartTimeRemaining--;

						if (this.recordAutoStartTimeRemaining > 0) {
							this.showAutoRecordTimer = true;
						} else {
							this.showAutoRecordTimer = false;
							if (!this.isRecording && !this.isRecorded) {
								this.startVoiceRecording();
							}
						}
					}

				}

				if (this.timeRemaining === 0) {
					this.timeRemaining = undefined;
					this.showTimer = false;
					this.timeLimitSubscription.unsubscribe();
					this.nextQuestion();
				}
			}
		});
	}

	resetVariables() {
		this.waitTimeout = 0;
		this.showTimer = false;
		this.allowNext = false;
		this.isRecording = false;
		this.isRecorded = false;
		this.timeRemaining = undefined;
		this.selectedOptions = [];
		this.selectedRangeOption = null;
		this.showAutoRecordTimer = false;
		this.voice_start_time = 0;
		this.voice_end_time = 0;

		if (this.timeLimitSubscription) {
			this.timeLimitSubscription.unsubscribe();
		}
	}

	stopRecording() {
		this.isRecording = false;
		this.streamService.isRecording = false;
		
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }

		if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
    }
    
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.srcObject = null;
    }

		this.streamService.stopStreaming();
		this.showVideoUploading = true;
  }

	getResponseAnswer() {
		switch (this.currentQuestion.response.response_type) {
      case 'RANGE':
        return {
					response_type: this.currentQuestion.response.response_type,
					response_range: this.selectedRangeOption
				}
      case 'SELECT':
        return {
					response_type: this.currentQuestion.response.response_type,
					response_select: {
						multi_select: this.currentQuestion.response.response_select?.multi_select,
						response_selected: this.selectedOptions
					}
				}
      case 'VISUAL':
        return {
					response_type: this.currentQuestion.response.response_type
				}
      case 'VOICE':
				return {
					response_type: this.currentQuestion.response.response_type,
					voice_start_time: this.voice_start_time,
					voice_end_time: this.voice_end_time
				}
			default:
				return null;
    }
	}

	onSelectResponseChange($event: any, selectedOption: any) {
    if (this.currentQuestion.response.response_select?.multi_select) {
      if ($event.target.checked) {
        this.selectedOptions.push(selectedOption);
      } else {
        const index = this.selectedOptions.indexOf(selectedOption);
        if (index !== -1) {
          this.selectedOptions.splice(index, 1);
        }
      }
    } else {
			if (this.selectedOptions.length > 0) {
				this.selectedOptions[0] = selectedOption;
			} else {
				this.selectedOptions.push(selectedOption);
			}
    }

		if (this.selectedOptions.length > 0) {
			this.allowNext = true;
		}
  }

	checkValueInSelectedOptionList(option: any) : boolean {
    return this.selectedOptions.find(item => item.label === option.label);
  }

	onRangeOptionSelected($event: any, count: any) {
    this.selectedRangeOption = count;
		if (this.selectedRangeOption) {
			this.allowNext = true;
		}
  }

	counter(length: number) {
		return Array.from({ length }, (_, i) => i + 1);
  }

	formatTime(seconds: number): string {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
	}

	getNumbers(startNumber: number, endNumber: number): number[] {
    return Array(endNumber - startNumber + 1).fill(0).map((_, index) => startNumber + index);
  }
}
